import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Message, MessageAttachment } from "../../types/Agent";
import AgentInput, { PendingFile, UploadedFile } from "./AgentInput";
import AgentMessage from "./AgentMessage";
import { ReactComponent as ErrorIconSVG } from '../../assets/images/error-icon.svg';
import { ReactComponent as DownArrowSVG } from '../../assets/images/down-arrow.svg';
import Button from "../Button";
import { BACK4APP_DOT_COM_SITE_URL } from "../../settings";
import useIsMobile from "../../hooks/useIsMobile";

const Agent = forwardRef<
  { setInputContent: (content: string) => void },
  {
    agentId: string;
    chatId: string;
    messages?: Message[];
    isSidebarOpen: boolean;
    paymentErrorMessage?: string;
    onUpgrade?: () => void;
    isBusy?: boolean;
    onSend: (content: string, uploadedFiles?: UploadedFile[]) => Promise<boolean>;
    onStop?: () => Promise<boolean>;
    onAttachmentClick?: (attachment: MessageAttachment) => void;
    inputContent?: string;
    onInputContentChange?: (value: string) => void;
    pendingFiles?: PendingFile[];
    onPendingFilesChange?: (
      files: PendingFile[] | ((prev: PendingFile[]) => PendingFile[])
    ) => void;
    showAttachments?: boolean;
    scrollToBottomOffsetRem?: number;
  }
>(({ agentId, chatId, messages, onSend, isSidebarOpen, paymentErrorMessage, onUpgrade, isBusy, onStop, onAttachmentClick, inputContent, onInputContentChange, pendingFiles, onPendingFilesChange, showAttachments, scrollToBottomOffsetRem }, ref) => {
  const handleSend = async (content: string, uploadedFiles: UploadedFile[]) => {
    if (isBusy) return false;
    return onSend(content, uploadedFiles);
  };
  const [autoScroll, setAutoScroll] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const isMobile = useIsMobile();
  const [scrollButtonOffsetRem, setScrollButtonOffsetRem] = useState(8); // Tracks banner visibility changes (desktop only)
  const boxRef = useRef<HTMLDivElement>(null);
  const agentInputRef = useRef<{ setContent: (content: string) => void }>(null);
  
  // Calculate base offset: mobile = 12rem, desktop = 8rem
  const baseOffsetRem = isMobile ? 12 : 8;
  
  // Calculate effective offset: 
  // - If prop is provided (mobile), use it directly
  // - Otherwise (desktop), use state which tracks banner visibility changes
  const effectiveScrollButtonOffsetRem = scrollToBottomOffsetRem !== undefined 
    ? scrollToBottomOffsetRem 
    : scrollButtonOffsetRem;

  // Update state to match base when mobile state changes (desktop only, when not showing banner)
  useEffect(() => {
    if (scrollToBottomOffsetRem === undefined && scrollButtonOffsetRem !== 10.5) {
      setScrollButtonOffsetRem(baseOffsetRem);
    }
  }, [isMobile, scrollToBottomOffsetRem, baseOffsetRem]);

  useImperativeHandle(ref, () => ({
    setInputContent: (content: string) => {
      if (agentInputRef.current) {
        agentInputRef.current.setContent(content);
      }
    }
  }));

  const updateScrollState = useCallback(() => {
    const element = boxRef.current;
    if (!element) return;

    const isScrollable = element.scrollHeight - element.clientHeight > 8;
    const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 8;

    setAutoScroll(isAtBottom);
    setShowScrollToBottom(isScrollable && !isAtBottom);
  }, []);

  useEffect(
    () => {
      if (messages && autoScroll && boxRef.current) {
        boxRef.current.scrollTop = boxRef.current.scrollHeight;
      }
      requestAnimationFrame(updateScrollState);
    },
    [
      messages,
      autoScroll,
      updateScrollState
    ]
  );

  const handleScrollToBottom = () => {
    const element = boxRef.current;
    if (!element) return;
    element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    setAutoScroll(true);
    setShowScrollToBottom(false);
  };

  const handleCreditsBannerVisibilityChange = useCallback((isVisible: boolean) => {
    if (scrollToBottomOffsetRem !== undefined) return;
    const baseOffset = isMobile ? 12 : 8;
    setScrollButtonOffsetRem(isVisible ? 10.5 : baseOffset);
  }, [scrollToBottomOffsetRem, isMobile]);

  return (
    <div className="relative h-full w-full">
      <div className="h-full w-full overflow-y-scroll glass-scroll" ref={boxRef} onScroll={updateScrollState}>
        {/* Maximize horizontal space for messages (no side paddings) */}
        <div className="w-full px-0 pb-[12rem] py-4">
          <AgentMessage
            message={{
              id: 'welcome',
              role: 'assistant',
              content:  `Hi, I'm your Web App Builder.
              Tell me what you want to build and I’ll create a working app for you.`
            }}
          />
          {messages?.map(message => <AgentMessage key={message.id} message={message} onAttachmentClick={onAttachmentClick} />)}
          {paymentErrorMessage && (
            <div className="grow flex flex-col items-center justify-center mt-10">
            <ErrorIconSVG />
            <div className="mt-4 font-sora font-semibold text-lg leading-[140%] text-light-grey mb-8">
              {paymentErrorMessage}
            </div>
            <Button
              type='submit'
              onClick={() => {
                if (onUpgrade) {
                  onUpgrade();
                  return;
                }
                window.open(`${BACK4APP_DOT_COM_SITE_URL}/pricing/agent?appId=${agentId}&type=agent`, '_blank', 'noopener,noreferrer');
              }}
            >
              Pay Now
            </Button>
          </div>
          )}
        </div>
      </div>
      {showScrollToBottom && (
        <button
          type="button"
          onClick={handleScrollToBottom}
          className={`absolute right-5 z-20 rounded-md p-2 !bg-[#222832] text-white/80 hover:text-white hover:border-white/20 shadow-lg transition ${
            effectiveScrollButtonOffsetRem === 10.5 
              ? 'bottom-[12rem] lg:bottom-[10.5rem]'
              : 'bottom-[9.5rem] lg:bottom-[8rem]'
          }`}
          aria-label="Scroll to bottom"
          title="Scroll to bottom"
        >
          <DownArrowSVG className="mx-auto h-3 w-3" />
        </button>
      )}
      {/* Hidden on mobile - mobile has its own input in MyAgents.tsx */}
      <div className="hidden lg:block">
        <AgentInput
          chatId={chatId}
          onSend={handleSend}
          onStop={onStop}
          onUpgrade={onUpgrade}
          onCreditsBannerVisibilityChange={handleCreditsBannerVisibilityChange}
          ref={agentInputRef}
          disabled={isBusy}
          isResponding={isBusy}
          value={inputContent}
          onValueChange={onInputContentChange}
          pendingFiles={pendingFiles}
          onPendingFilesChange={onPendingFilesChange}
          showAttachments={showAttachments}
        />
      </div>
    </div>
  )
});

export default Agent;
